import { useEffect } from 'react';
import { create } from 'zustand';
import axiosInstance from '../../../../../fetchConfig/axiosConfig';
import logger from '../../../../../utils/logger';

// The print queue lives on the server (src/print_service). It sends one document at a time in order,
// checks each one actually printed, and halts + emails if anything doesn't. This store mirrors the
// batches this browser started; their ids sit in localStorage so a reload or reopen reattaches.
// To test without paper, add a sim:// printer to FeatureMap.PRINTERS (see simulator.py).

const POLL_MS = 2500;
const IDS_KEY = 'printBatchIds';
const UPLOAD_TRIES = 5;

export const STATE_LABEL = {
  uploading: 'Uploading',
  queued: 'Waiting its turn',
  sending: 'Sending',
  pending: 'Queued at printer',
  'pending-held': 'Held at printer',
  processing: 'Printing',
  'processing-stopped': 'Paused',
  completed: 'Printed',
  failed: "Didn't print",
  canceled: 'Canceled',
  skipped: 'Skipped',
};

const PRINTING = new Set(['processing', 'processing-stopped']);

export const isPrinting = (j) => PRINTING.has(j.state);
export const isQueueActive = (q) => q.batches.some((b) => b.status !== 'finished');

// Sheets a job uses: our own count when pages are known, otherwise the printer's.
export const sheetsOf = (j) => (j.sheetsPerCopy != null ? j.sheetsPerCopy * j.copies : j.printerSheets || 0);

const usePrintQueueStore = create(() => ({ batches: [], jobs: [], busy: 0, error: '' }));
const { getState, setState } = usePrintQueueStore;

const readIds = () => {
  try {
    const ids = JSON.parse(localStorage.getItem(IDS_KEY));
    return Array.isArray(ids) ? ids : [];
  } catch {
    return [];
  }
};
const writeIds = (ids) => localStorage.setItem(IDS_KEY, JSON.stringify([...new Set(ids)]));

// File bytes stay in memory only so a failed upload can be retried from the halt banner.
const bytesByKey = new Map();
const keyOf = (batchId, index) => `${batchId}:${index}`;
const cancelled = new Set();

function setBatches(batches) {
  const jobs = batches.flatMap((b) => b.jobs.map((j) => ({
    ...j,
    key: keyOf(b.id, j.index),
    batchId: b.id,
    printerName: b.printerName,
    sheetsPerCopy: Number.isFinite(j.pages) ? (b.duplex ? Math.ceil(j.pages / 2) : j.pages) : null,
  })));
  setState({ batches, jobs });
  batches.filter((b) => b.status === 'finished').forEach((b) => b.jobs.forEach((j) => bytesByKey.delete(keyOf(b.id, j.index))));
}

const errorText = (e) => e?.response?.data?.error || e?.message || 'unknown error';

const api = {
  create: async (body) => (await axiosInstance.post('/print_batches', body)).data,
  upload: (batchId, index, bytes, name) => {
    const form = new FormData();
    form.append('file', new Blob([bytes], { type: 'application/pdf' }), /\.pdf$/i.test(name) ? name : `${name}.pdf`);
    return axiosInstance.put(`/print_batches/${batchId}/documents/${index}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  uploadFailed: (batchId, index, error) => axiosInstance.post(`/print_batches/${batchId}/documents/${index}/failed`, { error }),
  list: async (ids) => (await axiosInstance.get('/print_batches', { params: { ids: ids.join(',') } })).data,
  cancel: (batchId, scope) => axiosInstance.post(`/print_batches/${batchId}/cancel`, { scope }),
  resolve: (batchId, action) => axiosInstance.post(`/print_batches/${batchId}/resolve`, { action }),
};

// ---------- syncing with the server ----------

let refreshing = null;
let pruneClean = false;

export function refresh() {
  if (refreshing) return refreshing;
  const ids = readIds();
  if (!ids.length) {
    setBatches([]);
    return Promise.resolve();
  }
  refreshing = api.list(ids)
    .then(({ batches, missing = [] }) => {
      // Merge rather than replace, so a batch created while this request was in flight isn't dropped.
      const byId = new Map(getState().batches.map((b) => [b.id, b]));
      batches.forEach((b) => byId.set(b.id, b));
      let keep = readIds().filter((id) => !missing.includes(id) && byId.has(id));
      if (pruneClean) {
        pruneClean = false;
        keep = keep.filter((id) => byId.get(id).status !== 'finished' || byId.get(id).jobs.some((j) => j.state !== 'completed'));
      }
      writeIds(keep);
      setState({ error: '' });
      setBatches(keep.map((id) => byId.get(id)));
    })
    .catch((e) => setState({ error: `Can't reach the print server right now: ${errorText(e)}. The print carries on regardless.` }))
    .finally(() => { refreshing = null; });
  return refreshing;
}

// Polls while the dialog is open and anything is still going. Printing continues with the dialog closed.
export function usePrintQueue(open) {
  useEffect(() => {
    if (!open) return undefined;
    let stopped = false;
    let timer;
    const tick = async () => {
      const { batches } = getState();
      if (readIds().length && (!batches.length || batches.some((b) => b.status !== 'finished'))) await refresh();
      if (!stopped) timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [open]);

  return usePrintQueueStore();
}

// ---------- starting a print ----------

export async function startBatch({ printerId, jobs, duplex = true }) {
  const batch = await api.create({
    printerId: printerId || '',
    duplex,
    documents: jobs.map((j) => ({ name: j.name, copies: j.copies, pages: Number.isFinite(j.pages) ? j.pages : null })),
  });
  jobs.forEach((j, i) => bytesByKey.set(keyOf(batch.id, i), { bytes: j.bytes, name: j.name }));
  // A new print joins anything still going; finished ones drop off the panel.
  const live = getState().batches.filter((b) => b.status !== 'finished');
  writeIds([...live.map((b) => b.id), batch.id]);
  setBatches([...live, batch]);
  return batch.id;
}

async function uploadOne(batchId, index) {
  const file = bytesByKey.get(keyOf(batchId, index));
  if (!file) throw new Error('This browser no longer has the file. Print it again from the dialog');
  for (let attempt = 1; ; attempt++) {
    try {
      await api.upload(batchId, index, file.bytes, file.name);
      return;
    } catch (e) {
      // Retries network drops, 5xx, 408 and 429; any other 4xx won't get better by waiting.
      const status = e?.response?.status;
      if (attempt >= UPLOAD_TRIES || (status < 500 && ![408, 429].includes(status))) throw e;
      await new Promise((resolve) => { setTimeout(resolve, 1000 * 2 ** attempt); });
    }
  }
}

// Uploads in order. The server starts printing #1 as soon as it lands and never sends #n before #n-1,
// so a failed upload just pauses the print at that document (and emails) instead of leaving a gap.
export async function uploadBatch(batchId, count, onProgress) {
  let failed = 0;
  for (let i = 0; i < count; i++) {
    if (cancelled.has(batchId)) break;
    onProgress?.(i);
    try {
      await uploadOne(batchId, i);
    } catch (e) {
      if (e?.response?.status === 409) break; // canceled on the server
      failed++;
      const size = bytesByKey.get(keyOf(batchId, i))?.bytes?.length || 0;
      const reason = e?.response?.status === 413 ? `the file is too large (${(size / 1e6).toFixed(1)} MB) for the server's upload limit` : errorText(e);
      logger.error(e);
      await api.uploadFailed(batchId, i, `Upload failed: ${reason}`).catch(logger.error);
    }
  }
  await refresh();
  return failed;
}

// ---------- controls ----------

const act = async (fn) => {
  setState((q) => ({ busy: q.busy + 1 }));
  try {
    await fn();
    await refresh();
  } catch (e) {
    logger.error(e);
    setState({ error: errorText(e) });
  } finally {
    setState((q) => ({ busy: Math.max(0, q.busy - 1) }));
  }
};

// Stops everything not yet printing. The document on the glass finishes unless includeCurrent is set.
export const cancelRemaining = ({ includeCurrent = false } = {}) => act(async () => {
  const live = getState().batches.filter((b) => b.status !== 'finished');
  live.forEach((b) => cancelled.add(b.id));
  await Promise.all(live.map((b) => api.cancel(b.id, includeCurrent ? 'all' : 'rest')));
});

// For a print paused on a document that didn't print: 'retry' | 'skip' | 'printed'.
export const resolveHalt = (batchId, action) => act(async () => {
  const b = getState().batches.find((x) => x.id === batchId);
  const j = b?.halt && b.jobs[b.halt.index];
  if (action === 'retry' && j && !j.hasFile) await uploadOne(batchId, j.index);
  await api.resolve(batchId, action);
});

export const canRetry = (batchId, j) => Boolean(j?.hasFile || bytesByKey.has(keyOf(batchId, j?.index)));

// onlyClean (used when the dialog opens): drop batches where everything printed, keep any with problems.
export function clearFinished({ onlyClean = false } = {}) {
  if (onlyClean) {
    pruneClean = true;
    return refresh();
  }
  const live = getState().batches.filter((b) => b.status !== 'finished');
  writeIds(live.map((b) => b.id));
  setBatches(live);
  return Promise.resolve();
}
